import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

const STORAGE_KEY = 'leora_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

// Generate a unique key for a cart item based on product + presentation + color
function itemKey(item) {
  return `${item.productId}_${item.precioId || ''}_${item.colorId || ''}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((product, selectedPrice, selectedColor, selectedPhoto) => {
    setItems((prev) => {
      const newItem = {
        productId: product.leora_productos_UUID,
        nombre: product.nombre,
        marca: product.marca,
        imagen: product.fotos?.[selectedPhoto]?.imagen || product.imagen_principal || '',
        precioId: selectedPrice?.id || null,
        precioDescripcion: selectedPrice?.descripcion || '',
        precioValor: selectedPrice?.valor || product.precio_minimo,
        colorId: selectedColor?.leora_colores_UUID || null,
        colorNombre: selectedColor?.nombre || '',
        colorHex: selectedColor?.codigo_hex || '',
        cantidad: 1,
      };

      const key = itemKey(newItem);
      const existing = prev.findIndex((i) => itemKey(i) === key);

      if (existing !== -1) {
        const updated = [...prev];
        updated[existing] = {
          ...updated[existing],
          cantidad: updated[existing].cantidad + 1,
        };
        return updated;
      }

      return [...prev, newItem];
    });
  }, []);

  const removeItem = useCallback((productId, precioId, colorId) => {
    setItems((prev) =>
      prev.filter(
        (i) =>
          itemKey(i) !== `${productId}_${precioId || ''}_${colorId || ''}`
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, precioId, colorId, cantidad) => {
    if (cantidad < 1) return;
    setItems((prev) =>
      prev.map((i) =>
        itemKey(i) === `${productId}_${precioId || ''}_${colorId || ''}`
          ? { ...i, cantidad }
          : i
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.cantidad, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.precioValor * i.cantidad, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalPrice }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
